import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { KIKI } from "@/constants/testIds";
import { useT, useLang } from "@/lib/i18n";

export function Fees({ onEnquire }) {
  const t = useT();
  const { lang } = useLang();
  const isHi = lang === "hi";
  const plans = t("fees.plans");

  return (
    <section id="fees" data-testid={KIKI.fees} className="relative py-16 md:py-24 bg-secondary/50">
      <div className="max-w-[90rem] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-10 md:mb-14">
          <div className="col-span-12 md:col-span-8">
            <SectionHeading
              index="09"
              overline={t("fees.overline")}
              overlineClassName={isHi ? "font-hindi tracking-normal text-[12px]" : ""}
            />
            <motion.h2
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className={`mt-5 font-black text-4xl sm:text-5xl md:text-6xl leading-[0.98] tracking-tight ${isHi ? "font-hindi" : "font-sans"}`}
            >
              {t("fees.titleA")}
              <span className={isHi ? "font-hindi" : "font-display italic font-normal"}>{t("fees.titleB")}</span>
            </motion.h2>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className={`text-muted-foreground text-sm md:text-base leading-relaxed max-w-sm md:ml-auto ${isHi ? "font-hindi" : ""}`}>
              {t("fees.lead")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
          {plans.map((p, i) => {
            const featured = i === 1;
            return (
              <motion.article
                key={p.name}
                data-testid={`${KIKI.feePlan}-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.85, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className={`brochure-card card-hover flex flex-col relative overflow-hidden border ${
                  featured
                    ? "bg-foreground text-background border-foreground md:-my-3 md:py-9"
                    : "bg-card text-foreground border-border"
                }`}
              >
                {featured && (
                  <span className={`self-start bg-[#FFCC00] text-black font-mono text-[10px] tracking-[0.18em] px-3 py-1.5 rounded-md mb-5 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>
                    {t("fees.popular")}
                  </span>
                )}

                <div className={featured ? "pb-5 border-b border-background/20" : "pb-5 border-b border-border"}>
                  <h3 className={`font-extrabold text-2xl md:text-[26px] leading-tight tracking-tight ${isHi ? "font-hindi" : "font-sans"}`}>{p.name}</h3>
                  <div className={`mt-3 font-black text-2xl md:text-3xl leading-none tracking-tight ${featured ? "text-[#FFCC00]" : "text-foreground"} ${isHi ? "font-hindi" : "font-sans"}`}>
                    {p.priceLabel}
                  </div>
                  <p className={`mt-3 text-sm leading-relaxed ${featured ? "text-background/70" : "text-muted-foreground"} ${isHi ? "font-hindi" : ""}`}>{p.sub}</p>
                </div>

                <ul className="py-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className={`flex items-start gap-3 text-[14px] leading-snug ${featured ? "text-background/90" : "text-foreground/80"} ${isHi ? "font-hindi" : ""}`}>
                      <span className={`mt-0.5 w-5 h-5 shrink-0 grid place-items-center rounded-full ${featured ? "bg-[#FFCC00] text-black" : "bg-muted text-foreground"}`}>
                        <Check className="w-3 h-3" strokeWidth={2.4} />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={onEnquire}
                  data-testid={`${KIKI.feePlanCta}-${i}`}
                  className={`mt-auto w-full group inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-[13px] font-semibold transition-colors ${
                    featured
                      ? "bg-[#FFCC00] text-black hover:bg-[#ffd633]"
                      : "btn-pill"
                  } ${isHi ? "font-hindi" : ""}`}
                >
                  {p.cta}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
                </button>
              </motion.article>
            );
          })}
        </div>

        <p className={`mt-6 text-xs text-muted-foreground max-w-2xl ${isHi ? "font-hindi" : "font-mono"}`}>{t("fees.note")}</p>
      </div>
    </section>
  );
}
